const BUN_API = process.env.REACT_APP_BUN_API || "http://localhost:5001";

/**
 * Get appropriate icon for a device based on its name
 * @param {string} deviceName - Name of the device
 * @returns {string} Material UI icon name
 */
const getDeviceIcon = (deviceName) => {
  const name = deviceName.toLowerCase();
  
  // Medical/EEG monitoring devices
  if (name.includes("conox")) {
    return "monitor_heart"; // Medical monitoring device icon
  }
  
  // Emulator/Simulation devices
  if (name.includes("emulator") || name.includes("simulator") || name.includes("test")) {
    return "developer_mode"; // Development/emulation icon
  }
  
  // Default fallback
  return "sensors";
};

class DeviceService {
  /**
   * Fetch available devices by scanning TCP servers
   * @returns {Promise<Array<{id: string, name: string, host: string, port: number, reachable: boolean, latency?: number, icon: string, description: string}>>}
   */
  async getAvailableDevices() {
    try {
      // Request all devices including unreachable ones so users can see what's configured
      const response = await fetch(`${BUN_API}/devices/scan?includeUnreachable=true`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to scan devices: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("[DeviceService] Received devices:", data);
      
      // Transform the response to match the expected format
      const transformedDevices = data.devices.map((device) => {
        // Normalize latency: ensure minimum of 1ms if latency exists and is 0 or very low
        const normalizedLatency = device.latency !== null && device.latency !== undefined
          ? Math.max(1, device.latency)
          : null;
        
        // Build description with validation info
        let description = device.reachable
          ? `Connected to ${device.host}:${device.port}${normalizedLatency ? ` (${normalizedLatency}ms)` : ""}`
          : `Unable to connect to ${device.host}:${device.port}${device.error ? ` - ${device.error}` : ""}`;
        
        // Add data validation info if available
        if (device.reachable && device.has_data !== null) {
          const statusParts = [];
          if (device.state) {
            statusParts.push(`State: ${device.state}`);
          }
          if (device.signals_valid !== null) {
            statusParts.push(device.signals_valid ? "Signals: Valid" : "Signals: Invalid");
          }
          if (device.has_data !== null) {
            statusParts.push(device.has_data ? "Data: Available" : "Data: Not available");
          }
          if (statusParts.length > 0) {
            description += ` | ${statusParts.join(", ")}`;
          }
        }
        
        return {
          id: device.id,
          name: device.name,
          host: device.host,
          port: device.port,
          reachable: device.reachable,
          latency: normalizedLatency,
          icon: getDeviceIcon(device.name), // Icon based on device type
          description: description,
          // Include validation fields
          signals_valid: device.signals_valid,
          has_data: device.has_data,
          state: device.state,
          state_code: device.state_code,
          validation_details: device.validation_details,
        };
      });
      
      console.log("[DeviceService] Transformed devices:", transformedDevices);
      return transformedDevices;
    } catch (error) {
      console.error("Error fetching devices:", error);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Get device list configuration (without scanning)
   * @returns {Promise<Array<{id: string, name: string, host: string, port: number}>>}
   */
  async getDeviceList() {
    try {
      const response = await fetch(`${BUN_API}/devices/list`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get device list: ${response.statusText}`);
      }

      const data = await response.json();
      return data.devices;
    } catch (error) {
      console.error("Error fetching device list:", error);
      return [];
    }
  }

  /**
   * Connect to a device and start streaming EEG data
   * @param {string} host - Device hostname or IP address
   * @param {number} port - Device port number
   * @returns {Promise<{status: string, host: string, port: number}>}
   */
  async connectDevice(host, port) {
    try {
      const response = await fetch(`${BUN_API}/devices/connect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ host, port }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || errorData.details || `Failed to connect device: ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("[DeviceService] Device connected:", data);
      return data;
    } catch (error) {
      console.error("Error connecting device:", error);
      throw error; // Re-throw so caller can handle it
    }
  }

  /**
   * Disconnect from current device and stop streaming
   * @returns {Promise<{status: string}>}
   */
  async disconnectDevice() {
    try {
      const response = await fetch(`${BUN_API}/devices/disconnect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || errorData.details || `Failed to disconnect device: ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("[DeviceService] Device disconnected:", data);
      return data;
    } catch (error) {
      console.error("Error disconnecting device:", error);
      throw error; // Re-throw so caller can handle it
    }
  }

  /**
   * Get current device connection status
   * @returns {Promise<{running: boolean, connected: boolean, host?: string, port?: number, packet_count?: number, last_error?: string}>}
   */
  async getDeviceStatus() {
    try {
      const response = await fetch(`${BUN_API}/devices/status`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get device status: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("[DeviceService] Device status:", data);
      return data;
    } catch (error) {
      console.error("Error fetching device status:", error);
      throw error; // Re-throw so caller can handle it
    }
  }
}

const deviceService = new DeviceService();

export default deviceService;

